/**
 * Measurements of the Sizes page — Figma `Sizes · components` (01 Measurement
 * field, 06 Measure guide) and the `Sizes / Desktop` frames.
 *
 * Which fields show and in which group follows the macket per body; the API
 * values (`GET /user/parameters`) are always cm / kg, the page can show inches
 * and pounds and converts on the way in and out.
 */

import femBody from '@/assets/img/fair-profile/measure-guide/feminine/body.webp'
import femBust from '@/assets/img/fair-profile/measure-guide/feminine/bust.svg'
import femFoot from '@/assets/img/fair-profile/measure-guide/feminine/foot-length.svg'
import femHeight from '@/assets/img/fair-profile/measure-guide/feminine/height.svg'
import femHips from '@/assets/img/fair-profile/measure-guide/feminine/hips.svg'
import femInseam from '@/assets/img/fair-profile/measure-guide/feminine/inseam.svg'
import femShoulders from '@/assets/img/fair-profile/measure-guide/feminine/shoulders.svg'
import femSleeve from '@/assets/img/fair-profile/measure-guide/feminine/sleeve.svg'
import femUnderBust from '@/assets/img/fair-profile/measure-guide/feminine/under-bust.svg'
import femWaist from '@/assets/img/fair-profile/measure-guide/feminine/waist.svg'
import mascBody from '@/assets/img/fair-profile/measure-guide/masculine/body.webp'
import mascChest from '@/assets/img/fair-profile/measure-guide/masculine/chest.svg'
import mascFoot from '@/assets/img/fair-profile/measure-guide/masculine/foot-length.svg'
import mascHeight from '@/assets/img/fair-profile/measure-guide/masculine/height.svg'
import mascHips from '@/assets/img/fair-profile/measure-guide/masculine/hips.svg'
import mascInseam from '@/assets/img/fair-profile/measure-guide/masculine/inseam.svg'
import mascNeck from '@/assets/img/fair-profile/measure-guide/masculine/neck.svg'
import mascShoulders from '@/assets/img/fair-profile/measure-guide/masculine/shoulders.svg'
import mascSleeve from '@/assets/img/fair-profile/measure-guide/masculine/sleeve.svg'
import mascWaist from '@/assets/img/fair-profile/measure-guide/masculine/waist.svg'
import footIllustration from '@/assets/img/fair-profile/measure-guide/shared/foot.webp'
import weightIllustration from '@/assets/img/fair-profile/measure-guide/shared/weight.webp'

export type FairProfileV2Body = 'feminine' | 'masculine'
export type FairProfileV2Units = 'metric' | 'imperial'

/** Macket names of the fields; each maps to an API `measurementType`. */
export type FairProfileV2MeasureKey =
  | 'height'
  | 'weight'
  | 'bust'
  | 'chest'
  | 'underBust'
  | 'waist'
  | 'hips'
  | 'footLength'
  | 'inseam'
  | 'neck'
  | 'shoulders'
  | 'sleeve'

export interface FairProfileV2MeasureField {
  key: FairProfileV2MeasureKey
  /** `GET /user/parameters` type, camelised. */
  type: string
  group: 'basic' | 'additional'
}

const API_TYPE: Record<FairProfileV2MeasureKey, string> = {
  height: 'height',
  weight: 'weight',
  bust: 'chest',
  chest: 'chest',
  underBust: 'underChest',
  waist: 'waist',
  hips: 'hip',
  footLength: 'footLength',
  inseam: 'inseamLength',
  neck: 'neck',
  shoulders: 'shoulderWidth',
  sleeve: 'armLength',
}

/** Fields per body, in the macket's order (`Sizes / Desktop / Partial`, `Full, men`). */
const FIELDS: Record<FairProfileV2Body, [FairProfileV2MeasureKey, 'basic' | 'additional'][]> = {
  feminine: [
    ['height', 'basic'],
    ['weight', 'basic'],
    ['bust', 'basic'],
    ['underBust', 'basic'],
    ['waist', 'basic'],
    ['hips', 'basic'],
    ['footLength', 'basic'],
    ['inseam', 'additional'],
    ['shoulders', 'additional'],
    ['sleeve', 'additional'],
  ],
  masculine: [
    ['height', 'basic'],
    ['weight', 'basic'],
    ['chest', 'basic'],
    ['waist', 'basic'],
    ['hips', 'basic'],
    ['footLength', 'basic'],
    ['inseam', 'additional'],
    ['neck', 'additional'],
    ['shoulders', 'additional'],
    ['sleeve', 'additional'],
  ],
}

/**
 * The fields of one body that the API actually has (a field the API does not
 * return — e.g. `neck` today — is left out rather than shown dead).
 */
export function fairProfileV2MeasureFields(
  body: FairProfileV2Body,
  apiTypes: readonly string[],
): FairProfileV2MeasureField[] {
  const known = new Set(apiTypes)
  return FIELDS[body]
    .map(([key, group]) => ({ key, type: API_TYPE[key], group }))
    .filter((field) => known.has(field.type))
}

export function fairProfileV2Body(gender: string | null | undefined): FairProfileV2Body | null {
  if (gender === 'female') return 'feminine'
  if (gender === 'male') return 'masculine'
  return null
}

/* ── Units ─────────────────────────────────────────────────────────────── */

const CM_PER_INCH = 2.54
const LB_PER_KG = 2.20462

export type FairProfileV2UnitKind = 'length' | 'weight'

export function fairProfileV2UnitKind(key: FairProfileV2MeasureKey): FairProfileV2UnitKind {
  return key === 'weight' ? 'weight' : 'length'
}

/** The unit label shown after the value: cm / kg / in / lb. */
export function fairProfileV2UnitLabel(kind: FairProfileV2UnitKind, units: FairProfileV2Units): string {
  if (kind === 'weight') return units === 'imperial' ? 'lb' : 'kg'
  return units === 'imperial' ? 'in' : 'cm'
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

/** API (cm / kg) → what the field shows. Inches keep a decimal, pounds do not. */
export function toFairProfileV2Display(
  value: number | null,
  kind: FairProfileV2UnitKind,
  units: FairProfileV2Units,
): number | null {
  if (value === null || !Number.isFinite(value)) return null
  if (units === 'metric') return round(value, 1)
  return kind === 'weight' ? round(value * LB_PER_KG, 0) : round(value / CM_PER_INCH, 1)
}

/** What the user typed → API (cm / kg). */
export function fromFairProfileV2Display(
  value: number | null,
  kind: FairProfileV2UnitKind,
  units: FairProfileV2Units,
): number | null {
  if (value === null || !Number.isFinite(value)) return null
  if (units === 'metric') return round(value, 1)
  return kind === 'weight' ? round(value / LB_PER_KG, 1) : round(value * CM_PER_INCH, 1)
}

/** "92", "92,5" or "" → a number or null (empty clears the field). */
export function parseFairProfileV2Input(raw: string): number | null | 'invalid' {
  const text = raw.trim().replace(',', '.')
  if (!text) return null
  const value = Number(text)
  return Number.isFinite(value) && value > 0 ? value : 'invalid'
}

/** Soft warning (Sizes states · 04): out of the usual range, the value still stands. */
export function isFairProfileV2OutOfRange(
  value: number | null,
  range: { min: number; max: number } | null | undefined,
): boolean {
  if (value === null || !range) return false
  return value < range.min || value > range.max
}

/* ── Measure guide (06) ────────────────────────────────────────────────── */

/** A drawn line over the body, in the 150 × 403 figure box. */
export interface FairProfileV2GuideOverlay {
  src: string
  left: number
  top: number
  width: number
  height: number
}

export type FairProfileV2GuideFigure =
  | { kind: 'body'; src: string; overlay: FairProfileV2GuideOverlay | null }
  | { kind: 'weight'; src: string }
  | { kind: 'foot'; src: string; overlay: FairProfileV2GuideOverlay }

/**
 * Figma gives each line its box and a stroke bleed (`inset`): a percent of the
 * box or a px value. Unfolded here once so the template places the image as is.
 */
function overlay(
  src: string,
  box: [number, number, number, number],
  bleedY: number | `${number}px`,
  bleedX: number | `${number}px`,
): FairProfileV2GuideOverlay {
  const [left, top, width, height] = box
  const dy = typeof bleedY === 'string' ? parseFloat(bleedY) : (height * bleedY) / 100
  const dx = typeof bleedX === 'string' ? parseFloat(bleedX) : (width * bleedX) / 100
  return { src, left: left - dx, top: top - dy, width: width + 2 * dx, height: height + 2 * dy }
}

const BODY: Record<FairProfileV2Body, string> = { feminine: femBody, masculine: mascBody }

const OVERLAYS: Record<FairProfileV2Body, Partial<Record<FairProfileV2MeasureKey, FairProfileV2GuideOverlay>>> = {
  feminine: {
    height: overlay(femHeight, [2, 8, 0, 386], 0.13, '3.68px'),
    bust: overlay(femBust, [47, 101, 58, 14], 3.57, 0.86),
    underBust: overlay(femUnderBust, [49, 117, 55, 7], 7.14, 0.91),
    waist: overlay(femWaist, [53, 139, 47, 14], 3.57, 1.06),
    hips: overlay(femHips, [39, 179, 74, 14], 3.57, 0.68),
    shoulders: overlay(femShoulders, [38, 81, 76, 0], '3.68px', 0.66),
    sleeve: overlay(femSleeve, [123, 81, 9, 111], 0.45, 37.94),
    inseam: overlay(femInseam, [76, 202, 0, 161], 0.31, '3.68px'),
  },
  masculine: {
    height: overlay(mascHeight, [2, 8, 0, 386], 0.13, '3.68px'),
    chest: overlay(mascChest, [38, 107, 74, 14], 3.57, 0.68),
    waist: overlay(mascWaist, [42, 146, 66, 14], 3.57, 0.76),
    hips: overlay(mascHips, [36, 192, 78, 14], 3.57, 0.64),
    shoulders: overlay(mascShoulders, [30, 78, 90, 0], '3.68px', 0.56),
    sleeve: overlay(mascSleeve, [121.5, 79.5, 8.5, 118.5], 0.42, 40.54),
    inseam: overlay(mascInseam, [75, 214, 0, 157], 0.32, '3.68px'),
    neck: overlay(mascNeck, [60, 58, 30, 5], 10, 1.67),
  },
}

const FOOT_OVERLAY: Record<FairProfileV2Body, FairProfileV2GuideOverlay> = {
  feminine: overlay(femFoot, [-10, 3, 0, 352], 0.14, '3.68px'),
  masculine: overlay(mascFoot, [-10, 3, 0, 352], 0.14, '3.68px'),
}

/** The picture for the field in focus (or the plain body with none). */
export function fairProfileV2GuideFigure(
  body: FairProfileV2Body,
  key: FairProfileV2MeasureKey | null,
): FairProfileV2GuideFigure {
  if (key === 'weight') return { kind: 'weight', src: weightIllustration }
  if (key === 'footLength') return { kind: 'foot', src: footIllustration, overlay: FOOT_OVERLAY[body] }
  return { kind: 'body', src: BODY[body], overlay: key ? (OVERLAYS[body][key] ?? null) : null }
}

/** `foot_length` / `footLength` → `footLength`: the wire is not always camelised. */
export function camelFairProfileV2Type(type: string): string {
  return type.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}
